/**
 * Comments.jsx
 *
 * Displays and allows posting of comments on an AE case.
 * Comments are stored in the 'AE Comments' SharePoint list (auto-created on first post).
 * Each comment can optionally include one file attachment.
 *
 * Props:
 *   caseId   - SharePoint AE list item ID
 *
 * Capability gates (canComment, canSeePrivateComments, etc.) come from the
 * AuthContext directly via useAuth() — never role strings, so custom roles
 * inherit the right public/private behaviour.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getComments, postComment, uploadCommentAttachment, fetchSpBlob, apiNotify, apiRequirementRequest, addCaseRequirement, getReferenceStaff, ackComment } from '../api/client.js';
import { getTherapieMapsData } from '../api/blobClient.js';
import { useAuth } from '../auth/AuthContext.jsx';
import FilePreview, { canPreview } from './FilePreview.jsx';
import SearchableSelect from './SearchableSelect.jsx';
import RichTextEditor from './RichTextEditor.jsx';
import { sanitizeRich, isHtml, richToText } from '../lib/richText.js';
import { validateUpload, ACCEPT_ATTR } from '../lib/uploadLimits.js';
import {
  ATTACHMENT_CATEGORIES,
  ATTACHMENT_CATEGORY_LABELS,
  getRequirements,
  categoryNeedsMetadata,
} from '../lib/eventVocabularies.js';
import {
  buildUploadFile,
  categoryToken,
  parseAttachmentName,
} from '../lib/attachmentNaming.js';
import { attachmentMetadataLine } from '../lib/attachmentMetadata.js';
import { formatDate, formatDateTime } from '../lib/dates.js';
import {
  ackSummary, commentSnoozeBadge, normaliseSnoozeDays, snoozeHelperText, snoozeDaysLabel,
  SNOOZE_DAY_PRESETS, SNOOZE_DAYS_MIN, SNOOZE_DAYS_MAX,
} from '../lib/sla.js';
import { MoonIcon } from '@phosphor-icons/react/dist/csr/Moon';
import SlotUploadModal from './SlotUploadModal.jsx';
import { findHoROsForRegion, findCMsForClinic } from '../lib/recipients.js';
import {
  REQ_OTHER_KEY, REQ_KIND_DOCUMENT, REQ_KIND_SIGNOFF,
  reqCategory, requirementKind, resolveUploadCategory,
} from '@shared/requirements.js';
import { isStaffEmail } from '@shared/notificationSchema.js';

// Stacking: the composer's pop-ups (ModalShell) sit at 10000; the two-step
// upload dialog opens from inside that flow, so it must sit above them.
const COMPOSER_MODAL_Z = 10000;
const UPLOAD_MODAL_Z   = COMPOSER_MODAL_Z + 1;

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function fileSizeLabel(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Escape user-supplied text before splicing it into the trusted confirmation
// line we append to a requirement comment.
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/**
 * ModalShell — shared focused pop-up used by the composer's Attachment,
 * Notification and Requirement flows. Backdrop click closes (unless `busy`);
 * children supply the body + their own footer buttons.
 */
function ModalShell({ title, onClose, busy, children, maxWidth = 460 }) {
  const close = busy ? undefined : onClose;
  return (
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: COMPOSER_MODAL_Z, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '22px 24px', width: '100%', maxWidth,
          maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', color: 'var(--text-3)', padding: 2, lineHeight: 1 }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Comments({
  caseId, onAttachmentAdded, onCommentPosted, refreshKey,
  existingAttachmentNames = [],
  caseRegion = null, caseClinic = null,
  caseTreatment = null, caseSeverity = null,
  // Case date of incident ('YYYY-MM-DD') — lower bound for a post photo's
  // "date taken" in the two-step upload, same as the case-file slots.
  dateOfIncident = null,
  // "Request a document" — only offered to roles that can manage requirements.
  // Sending creates the requirement slot (addCaseRequirement) AND notifies.
  canManageRequirements = false, requirementTypes = [], onRequirementAdded,
  // Per-case (admin-added) requirements on this case — the live DOCUMENT ones
  // are offered as upload targets ('req:<id>') so a requested file can ride on
  // a comment exactly as from the Required-docs card.
  requirements = [],
  // Fired after an "Acknowledge" lands so the case file can refresh History +
  // the response-SLA chip (an owner-side ack pauses the timer).
  onAcknowledged,
  // "Snooze notifications" (sla_escalation_logic.md §4.1): the composer control
  // is offered only on an SLA-eligible case (Treatment with a severity code, slaEnabled —
  // the case file decides) to users with manageSla, for PUBLIC messages. `sla`
  // is the case's response-SLA state (GET /cases/{id}/sla) so a snoozing
  // message can show "Snoozed until <date>" once the clinic acknowledged it.
  slaEligible = false,
  sla = null,
  // Only clinic-side viewers get the Acknowledge action, and only under
  // messages from the HQ side (comment.authorSide === 'hq' — Supervisors, HoROs,
  // Country Managers): never their own or a clinic colleague's. Everyone else
  // sees a read-only tick once the clinic has acknowledged (case file decides).
  canAcknowledge = false,
}) {
  const { user } = useAuth();
  const [comments, setComments]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const [commentText, setCommentText]   = useState('');
  const [isPublic, setIsPublic]         = useState(true);
  // Snooze: on/off + the days picker ('' = off; a preset number or 'custom').
  const [snoozeOn, setSnoozeOn]         = useState(false);
  const [snoozePick, setSnoozePick]     = useState(String(SNOOZE_DAY_PRESETS[3]));   // 4 days
  const [snoozeCustom, setSnoozeCustom] = useState('');
  // Pending attachments: [{ file, metadata }] — every file shares ONE category
  // (fileCategory) but carries its OWN upload metadata (post photo taken-on,
  // training certificate trainer + date; null for plain categories), the same
  // shape NewCaseWizard queues, so a second batch never overwrites the first.
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileCategory, setFileCategory] = useState('');   // category key for the attachment(s)
  const [otherLabel, setOtherLabel]     = useState('');   // text used when category === 'other'
  const [pickerOpen, setPickerOpen]     = useState(false); // category-first picker visibility
  const [metaModalOpen, setMetaModalOpen] = useState(false); // two-step file + details dialog
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState(null);
  const [attachError, setAttachError]   = useState(null);  // file-validation error, shown by the picker
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // "Acknowledge" a message — add-only, no notification; the server pauses the
  // response timer when the acknowledging user is on the owner side. On 409
  // (already acknowledged elsewhere) re-read the thread so the pill catches up.
  const handleAck = useCallback(async (commentId) => {
    try {
      const res = await ackComment(commentId);
      setComments(cs => cs.map(c => c.id === commentId ? { ...c, acks: res?.acks || c.acks } : c));
    } catch (e) {
      if (/already acknowledged/i.test(e.message || '')) {
        try {
          const fresh = await getComments(caseId);
          setComments(fresh);
        } catch { /* keep what we have */ }
      } else {
        throw e;
      }
    }
    onAcknowledged?.();
  }, [caseId, onAcknowledged]);

  // Category is valid (and "other" has a label) → user can pick a file.
  const categoryReady = !!fileCategory && (fileCategory !== 'other' || otherLabel.trim().length > 0);

  // Attachment category options — matrix-required for this case first, then
  // the live per-case DOCUMENT requirements ('req:<id>' — sign-offs take no
  // file), then the rest. Mirrors NewCaseWizard's attachmentOptions so the
  // comment attach UX uses the same vocabulary as the case submission.
  const matrixReqs      = getRequirements(caseTreatment, caseSeverity);
  const requiredUploads = matrixReqs?.uploads || [];
  const perCaseDocs     = (Array.isArray(requirements) ? requirements : [])
    .filter(r => r && r.id != null && requirementKind(r) === REQ_KIND_DOCUMENT);
  const perCaseByCategory = new Map(perCaseDocs.map(r => [reqCategory(r.id), r]));
  const attachmentOptions = (() => {
    const ordered = [];
    for (const k of requiredUploads) {
      const cat = ATTACHMENT_CATEGORIES.find(c => c.key === k);
      if (cat) ordered.push({ key: cat.key, label: cat.label, sub: 'Required for this case' });
    }
    for (const r of perCaseDocs) {
      ordered.push({ key: reqCategory(r.id), label: r.label || 'Requested document', sub: 'Requested on this case' });
    }
    for (const c of ATTACHMENT_CATEGORIES) {
      if (!ordered.find(o => o.key === c.key)) ordered.push({ key: c.key, label: c.label });
    }
    return ordered;
  })();
  // A per-case requirement uploads under its 'req:<id>' category but is
  // PROMPTED and validated as its canonical slot — a per-case "Training
  // certificate" asks for the training date + trainer exactly like the matrix
  // slot (shared requirements.js resolveUploadCategory; the server mirrors it,
  // and stores the raw 'req:<id>').
  const isReqCategory     = perCaseByCategory.has(fileCategory);
  const effectiveCategory = isReqCategory ? resolveUploadCategory(fileCategory, perCaseDocs) : fileCategory;
  const fileCategoryLabel = isReqCategory
    ? (perCaseByCategory.get(fileCategory)?.label || 'Requested document')
    : (ATTACHMENT_CATEGORY_LABELS[fileCategory] || fileCategory);

  // "+ Add" dropdown menu (Attachment / Notification / Requirement entry points).
  // The menu is PORTALLED (position: fixed, anchored to the button) so it can't be
  // clipped by the card's overflow and reads as attached to the "+ Add" button.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // { anchor: trigger rect, up: opens above the trigger, left: clamped x }
  const [addMenuPos,  setAddMenuPos]  = useState(null);
  const addBtnRef  = useRef(null);  // the "+ Add" trigger
  const addMenuRef = useRef(null);  // the portalled menu
  const ADD_MENU_GAP = 6;           // = --dd-offset
  const ADD_MENU_EDGE = 12;         // keep-out from the viewport edges
  function positionAddMenu() {
    const r = addBtnRef.current?.getBoundingClientRect();
    if (r) setAddMenuPos({ anchor: { top: r.top, bottom: r.bottom, left: r.left }, up: false, left: Math.round(r.left) });
  }
  function toggleAddMenu() {
    if (addMenuOpen) { setAddMenuOpen(false); return; }
    positionAddMenu();
    setAddMenuOpen(true);
  }
  // Once the menu has a size, keep it on screen: flip above the button when
  // the room below is short (and above is roomier), and pull it left of the
  // right edge. Runs before paint, so the flip is never visible.
  useLayoutEffect(() => {
    const el = addMenuRef.current;
    if (!addMenuOpen || !addMenuPos || !el) return;
    const { anchor } = addMenuPos;
    const h = el.offsetHeight, w = el.offsetWidth;
    const below = window.innerHeight - anchor.bottom - ADD_MENU_GAP - ADD_MENU_EDGE;
    const above = anchor.top - ADD_MENU_GAP - ADD_MENU_EDGE;
    const up   = h > below && above > below;
    const left = Math.max(ADD_MENU_EDGE, Math.min(Math.round(anchor.left), window.innerWidth - w - ADD_MENU_EDGE));
    if (up !== addMenuPos.up || left !== addMenuPos.left) setAddMenuPos(p => ({ ...p, up, left }));
  }, [addMenuOpen, addMenuPos]);
  useEffect(() => {
    if (!addMenuOpen) return;
    function onDown(e) {
      if (addBtnRef.current?.contains(e.target)) return;   // toggle handles the button
      if (addMenuRef.current?.contains(e.target)) return;  // clicks inside the menu
      setAddMenuOpen(false);
    }
    function reposition() { positionAddMenu(); }
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [addMenuOpen]);

  // Notify recipients — chosen in the Notification pop-up, staged onto the
  // pending comment and sent when it's posted.
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyEmails,  setNotifyEmails]  = useState(new Set());  // selected emails
  const [openCats,      setOpenCats]      = useState(new Set());  // expanded category dropdowns
  const toggleCat = (key) => setOpenCats(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const [notifyMessage, setNotifyMessage] = useState('');         // optional message to recipients
  const [customEmail,   setCustomEmail]   = useState('');

  // "Add requirement" — the pop-up no longer submits on its own. Picking a
  // document/sign-off STAGES it onto the pending comment (like an attachment),
  // and "Post comment" is what creates the requirement, records a confirming
  // comment ("Requested document: X"), and fires the notification.
  //   reqKind: '' (off) | 'document' | 'signoff'
  const [reqKind,        setReqKind]        = useState('');
  const [reqKey,         setReqKey]         = useState('');   // document type key
  const [reqOtherLabel,  setReqOtherLabel]  = useState('');   // document "other" free text
  const [reqSignoffLabel, setReqSignoffLabel] = useState(''); // sign-off task label
  const [reqError,       setReqError]       = useState(null);
  const [reqModalOpen,   setReqModalOpen]   = useState(false);
  // The staged requirement, applied on Post. { kind, key?, otherLabel?,
  // signoffLabel?, displayLabel, payload } — payload is what addCaseRequirement wants.
  const [stagedReq,      setStagedReq]      = useState(null);

  // TherapieMaps data — used to resolve HoRO (by region) and CM (by clinic)
  // for the current case. Cached at the module level by getTherapieMapsData,
  // so this is effectively a one-time fetch per browser session.
  const [tmData, setTmData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getTherapieMapsData()
      .then(d => { if (!cancelled) setTmData(d); })
      .catch(() => { /* recipient slots gracefully degrade to "—" */ });
    return () => { cancelled = true; };
  }, []);

  const horos = findHoROsForRegion(tmData, caseRegion);
  const cms   = findCMsForClinic(tmData, caseClinic);
  const horoEmail = horos[0]?.mail || null;
  const horoName  = horos[0]?.displayName || null;
  const cmEmail   = cms[0]?.mail || null;
  const cmName    = cms[0]?.displayName || null;

  // Directory staff (HSRC + Injectables Growth) for the recipient picker,
  // grouped by department. Fails soft to [] (picker just shows case contacts).
  const [deptStaff, setDeptStaff] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getReferenceStaff()
      .then(r => { if (!cancelled) setDeptStaff(r?.staff || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Group directory staff by department (only those with an email to notify).
  const staffByDept = {};
  for (const s of deptStaff) {
    if (!s.email) continue;
    (staffByDept[s.department || 'Other'] = staffByDept[s.department || 'Other'] || []).push(s);
  }

  // Case-specific recipients — resolved per case from TherapieMaps. Show the
  // actual person's name, with their role + location as the sub-line.
  const caseContacts = [
    { key: 'horo', email: horoEmail, name: horoName || 'Head of RO',     sub: ['Head of RO', caseRegion].filter(Boolean).join(' · ') },
    { key: 'cm',   email: cmEmail,   name: cmName   || 'Clinic Manager', sub: ['Clinic Manager', caseClinic].filter(Boolean).join(' · ') },
  ].filter(r => r.email);

  // Recipient picker categories — collapsed dropdowns (department groups + the
  // case contacts), each a multi-select. Keeps the picker to a few category
  // rows instead of a long flat list, and lets categories be shown/hidden per
  // scenario later (e.g. hide a department for certain event types).
  const notifyCategories = [
    ...Object.keys(staffByDept).sort().map(dept => ({
      key:    dept,
      label:  dept,
      people: staffByDept[dept].map(s => ({ email: s.email, name: s.name, sub: s.jobTitle })),
    })),
    ...(caseContacts.length ? [{
      key:    '__case',
      label:  'This case',
      people: caseContacts.map(r => ({ email: r.email, name: r.name, sub: r.sub })),
    }] : []),
  ];

  function toggleNotifyEmail(email) {
    setNotifyEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  }

  const canComment    = !!user?.canComment;
  const canRequestRequirements = canComment && !!canManageRequirements;
  // Capability, not role string — a custom role without privateView gets the
  // same public-only view a ClinicManager does (server strips regardless).
  const canSeePrivate = !!user?.canSeePrivateComments;
  // Snooze control: manageSla holders, SLA-eligible case, PUBLIC message only
  // (the server enforces the same three — 403 / 400).
  const canSnooze = canComment && slaEligible && !!user?.canManageSla;
  const snoozeVisible = canSnooze && isPublic;
  const snoozeDays = snoozeVisible && snoozeOn
    ? normaliseSnoozeDays(snoozePick === 'custom' ? snoozeCustom : snoozePick)
    : null;
  const snoozeInvalid = snoozeVisible && snoozeOn && snoozeDays == null;
  const slaHours = Number(sla?.hoursPerLeg) || 6;

  // ── Load comments ──────────────────────────────────────────────────────────
  const loadComments = useCallback(async () => {
    if (!caseId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getComments(caseId);
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadComments(); }, [loadComments, refreshKey]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    // Assume identity: the composer stays fully interactive for view parity,
    // but finishing the action as the assumed user is blocked (the API would
    // reject it anyway — this keeps the message friendly and nothing is sent).
    if (user?.impersonating) {
      setSubmitError(`You're viewing as ${user.email} — posting is disabled while assuming an identity. Exit "Assume identity" to post.`);
      return;
    }
    let html = sanitizeRich(commentText);
    // A typed comment is normally required, but a staged requirement provides the
    // content on its own (we auto-record "Requested … : X"), so allow posting then.
    if (!richToText(html) && !stagedReq) return;

    // Halt before posting if an attachment is invalid — the user fixes the
    // file and resubmits, rather than posting a comment with a failed upload.
    for (const { file } of attachedFiles) {
      const uploadErr = validateUpload(file.name, file.size);
      if (uploadErr) {
        setAttachError(attachedFiles.length > 1 ? `${file.name}: ${uploadErr}` : uploadErr);
        setAttachedFiles(prev => prev.filter(it => it.file !== file));
        setPickerOpen(true);
        return;
      }
    }

    // Mirror the server (postCommentAttachment): a requested document — a slot
    // category or a per-case requirement — must ride on a PUBLIC message, or the
    // clinic never sees it and it satisfies nothing. Catch it here rather than
    // post the comment and then fail every upload.
    if (attachedFiles.length && !isPublic && fileCategory && fileCategory !== 'other') {
      setSubmitError('Requested documents must be attached to a public message so the clinic can see them — switch the comment to Public, or attach the file as "Other".');
      return;
    }

    // Snooze switched on but the custom days are not a whole number 1–30.
    if (snoozeInvalid) {
      setSubmitError(`Snooze days must be a whole number between ${SNOOZE_DAYS_MIN} and ${SNOOZE_DAYS_MAX}.`);
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      // 1) If a requirement is staged, create it FIRST (the durable artifact) and
      //    append a confirmation line to the comment body so the thread records it.
      let reqLabel = null;
      if (stagedReq) {
        const res = await addCaseRequirement(caseId, stagedReq.payload);
        reqLabel = res?.requirement?.label || stagedReq.displayLabel;
        const kindWord = stagedReq.kind === REQ_KIND_SIGNOFF ? 'sign-off' : 'document';
        const line = `<p>Requested ${kindWord}: <strong>${escapeHtml(reqLabel)}</strong></p>`;
        html = richToText(html) ? `${html}${line}` : line;
      }

      const result = await postComment(
        caseId, html, isPublic,
        user?.name  || '',
        user?.email || '',
        { snoozeDays },
      );

      // Non-fatal problems that happen AFTER the comment is saved (attachment
      // upload, notification send). Collected so we surface ONE "Comment posted,
      // but …" message — and never a green success tick next to a real failure.
      const postWarnings = [];

      if (attachedFiles.length) {
        // Match case-submission rules exactly: category-driven rename to
        // AE{caseId}-{CategoryToken}[-N].{ext}, with the dupe counter taken
        // from existing case attachments (so a 2nd ConsentForm becomes -2)
        // and incremented per file in this batch.
        const catItem = {
          category: fileCategory,
          otherLabel: fileCategory === 'other' ? otherLabel.trim() : null,
        };
        const token = categoryToken(catItem);
        let dupes = existingAttachmentNames.reduce((n, name) => {
          const parsed = parseAttachmentName(name);
          return parsed?.token === token ? n + 1 : n;
        }, 0);
        const failed = [];
        for (const { file, metadata } of attachedFiles) {
          try {
            // A per-case requirement file keeps its own name — the Required-docs
            // card upload does the same, and a canonical AE{id}-{Token} name
            // would double-bucket it into a matrix slot by filename.
            const renamed = isReqCategory ? file : buildUploadFile({ ...catItem, file }, caseId, dupes);
            // Category travels explicitly (not just in the filename) so the
            // server's per-slot file counts see comment uploads too; metadata
            // is this file's own (only the two-step categories have any).
            await uploadCommentAttachment(result.id, renamed, { category: fileCategory, metadata: metadata || null });
            dupes++;
          } catch (uploadErr) {
            failed.push(`${file.name} (${uploadErr.message})`);
          }
        }
        if (failed.length < attachedFiles.length) {
          onAttachmentAdded?.();   // refresh the Attachments card in CaseDetail
        }
        if (failed.length) {
          // Comment was saved — surface the upload failure without rolling back
          postWarnings.push(`${failed.length === 1 ? 'an attachment' : `${failed.length} attachments`} failed to upload (${failed.join(', ')})`);
        }
      }

      // Fire notifications. No client/clinical data — case ref + clinic + link +
      // the officer's optional note only. Awaited (not fire-and-forget) so a
      // failed or partially-dropped send is surfaced instead of vanishing — the
      // comment is already saved, so a notify problem only WARNS, never rolls back.
      // result.id links the send to this comment so the timeline can fold it in.
      const allRecipients = [
        ...notifyEmails,
        ...(customEmail.trim() ? [customEmail.trim()] : []),
      ].filter(Boolean);
      if (allRecipients.length) {
        try {
          const res = stagedReq
            // Requirement-aware notification — tells the recipient they have an
            // action ("please provide X") rather than a plain comment ping.
            ? await apiRequirementRequest(caseId, allRecipients, reqLabel, notifyMessage || null, result.id)
            : await apiNotify(caseId, allRecipients, null, notifyMessage || null, result.id);
          if (res?.rejected?.length) {
            postWarnings.push(`${res.rejected.length} recipient${res.rejected.length > 1 ? 's were' : ' was'} not notified — not a valid staff address (${res.rejected.join(', ')})`);
          }
        } catch (notifyErr) {
          postWarnings.push(`the notification didn't send — ${notifyErr.message}`);
        }
      }

      if (stagedReq) onRequirementAdded?.();  // refresh the case so the new slot appears
      onCommentPosted?.();   // refresh History card in CaseDetail
      setCommentText('');
      setSnoozeOn(false);    // a snooze is per message — never carried to the next one
      setSnoozeCustom('');
      setAttachedFiles([]);
      setFileCategory('');
      setOtherLabel('');
      setMetaModalOpen(false);
      setAttachError(null);
      setPickerOpen(false);
      setNotifyModalOpen(false);
      clearNotify();
      clearStagedReq();
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (postWarnings.length) {
        setSubmitError(`Comment posted, but ${postWarnings.join('; and ')}.`);
      } else {
        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000);
      }
      await loadComments();
    } catch (err) {
      setSubmitError(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  }

  // "Add requirement": STAGE the document/sign-off onto the pending comment.
  // Nothing is created here — Post comment does that (see handleSubmit).
  function stageRequirement() {
    setReqError(null);
    if (reqKind === REQ_KIND_DOCUMENT) {
      if (!reqKey) { setReqError('Choose a document type.'); return; }
      if (reqKey === REQ_OTHER_KEY && !reqOtherLabel.trim()) { setReqError('Enter a label for the document.'); return; }
      const isOther = reqKey === REQ_OTHER_KEY;
      const displayLabel = isOther
        ? reqOtherLabel.trim()
        : (requirementTypes.find(t => t.key === reqKey)?.label || 'Document');
      setStagedReq({
        kind: REQ_KIND_DOCUMENT,
        key: reqKey,
        otherLabel: isOther ? reqOtherLabel.trim() : '',
        displayLabel,
        payload: { kind: REQ_KIND_DOCUMENT, key: reqKey, label: isOther ? reqOtherLabel.trim() : undefined },
      });
    } else {
      if (!reqSignoffLabel.trim()) { setReqError('Describe the task to sign off.'); return; }
      const displayLabel = reqSignoffLabel.trim();
      setStagedReq({
        kind: REQ_KIND_SIGNOFF,
        signoffLabel: displayLabel,
        displayLabel,
        payload: { kind: REQ_KIND_SIGNOFF, label: displayLabel },
      });
    }
    setReqModalOpen(false);
  }

  // Open the pop-up. If something is already staged, re-populate the form so the
  // chip's "Edit" reopens it as the user left it; otherwise start fresh.
  function openReqModal() {
    setAddMenuOpen(false);
    setReqError(null);
    if (stagedReq) {
      setReqKind(stagedReq.kind);
      if (stagedReq.kind === REQ_KIND_DOCUMENT) {
        setReqKey(stagedReq.key);
        setReqOtherLabel(stagedReq.otherLabel || '');
      } else {
        setReqSignoffLabel(stagedReq.signoffLabel || stagedReq.displayLabel || '');
      }
    } else {
      setReqKind(REQ_KIND_DOCUMENT);
      // Default the document dropdown to the first configured type (skip "Other").
      setReqKey(requirementTypes[0]?.key || '');
      setReqOtherLabel('');
      setReqSignoffLabel('');
    }
    setReqModalOpen(true);
  }
  function closeReqModal() {
    setReqModalOpen(false);
    setReqError(null);
  }
  // Drop the staged requirement and reset the form.
  function clearStagedReq() {
    setStagedReq(null);
    setReqKind(REQ_KIND_DOCUMENT);
    setReqKey('');
    setReqOtherLabel('');
    setReqSignoffLabel('');
    setReqError(null);
  }

  // Clear any staged notification recipients/message.
  function clearNotify() {
    setNotifyEmails(new Set());
    setNotifyMessage('');
    setCustomEmail('');
  }
  const notifyCount = notifyEmails.size + (customEmail.trim() ? 1 : 0);

  // Shared recipient picker (category dropdowns + custom email) — used by BOTH the
  // inline "Notify someone" panel and the dialog's optional notify section.
  function renderRecipientPicker() {
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, alignItems: 'start' }}>
          {notifyCategories.map(cat => {
            const selCount = cat.people.filter(p => notifyEmails.has(p.email)).length;
            const isOpen   = openCats.has(cat.key);
            return (
              <div key={cat.key} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => toggleCat(cat.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '8px 10px', background: isOpen ? 'var(--surface-2)' : 'var(--surface)',
                    border: 'none', cursor: 'pointer', fontSize: 12, textAlign: 'left',
                  }}
                >
                  <span style={{ fontWeight: 600, color: 'var(--text)', flex: 1 }}>{cat.label}</span>
                  {selCount > 0 && <span className="count-chip">{selCount}</span>}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ width: 12, height: 12, color: 'var(--text-3)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {isOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 4, borderTop: '1px solid var(--border)' }}>
                    {cat.people.map(p => {
                      const sel = notifyEmails.has(p.email);
                      // A resolved address that isn't a valid staff email can't be
                      // notified (the server drops it) — flag it and block selection
                      // so a doomed recipient is visible BEFORE the comment is posted.
                      const bad = !isStaffEmail(p.email);
                      return (
                        <label
                          key={p.email}
                          className={`notify-opt${sel && !bad ? ' selected' : ''}`}
                          title={bad ? `${p.email} isn't a valid staff address — can't be notified` : p.email}
                          style={bad ? { opacity: 0.65 } : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={sel && !bad}
                            disabled={bad}
                            onChange={() => { if (!bad) toggleNotifyEmail(p.email); }}
                            style={{ accentColor: 'var(--brand)', width: 14, height: 14, flexShrink: 0 }}
                          />
                          <span className="notify-opt-name">{p.name}</span>
                          {p.sub && <span className="notify-opt-sub">· {p.sub}</span>}
                          {bad && (
                            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3, color: '#B45309', fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                              </svg>
                              can’t notify
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="email"
            className="input"
            placeholder="Add email address…"
            value={customEmail}
            onChange={e => setCustomEmail(e.target.value)}
            style={{ fontSize: 12, padding: '5px 10px', flex: 1 }}
          />
        </div>
      </>
    );
  }

  function handleFileChange(e) {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!files.length) return;
    // Category was chosen first; validate the files up-front so an unsupported
    // type is caught here — before the comment is sent — and the user can pick
    // different files. The picker stays open so they can retry; one bad file
    // rejects the whole selection so the outcome is predictable.
    for (const file of files) {
      const uploadErr = validateUpload(file.name, file.size);
      if (uploadErr) {
        setAttachError(files.length > 1 ? `${file.name}: ${uploadErr}` : uploadErr);
        return;
      }
    }
    setAttachError(null);
    setAttachedFiles(prev => [...prev, ...files.map(file => ({ file, metadata: null }))]);
    setPickerOpen(false);
  }

  // Two-step categories (post photo, training certificate): SlotUploadModal
  // returns the chosen files plus ONE metadata object for that batch, which is
  // stored on EACH of its files (a later batch carries its own). Files are
  // validated the same way as the plain picker; a bad file rejects the batch and
  // reopens the picker so the user can retry.
  function handleMetaUploadConfirm({ files, metadata } = {}) {
    const list = Array.from(files || []);
    setMetaModalOpen(false);
    if (!list.length) return;
    for (const file of list) {
      const uploadErr = validateUpload(file.name, file.size);
      if (uploadErr) {
        setAttachError(list.length > 1 ? `${file.name}: ${uploadErr}` : uploadErr);
        setPickerOpen(true);
        return;
      }
    }
    setAttachError(null);
    setAttachedFiles(prev => [...prev, ...list.map(file => ({ file, metadata: metadata || null }))]);
    setPickerOpen(false);
  }

  // Close the category picker. The shared category is only dropped when no
  // files are queued — cancelling a "add one more" pass must not orphan the
  // files already staged.
  function closePicker() {
    setPickerOpen(false);
    setMetaModalOpen(false);
    setAttachError(null);
    if (attachedFiles.length === 0) {
      setFileCategory('');
      setOtherLabel('');
    }
  }

  // "Choose files…" — two-step categories collect file(s) + metadata in their
  // own dialog, which REPLACES the picker (never stacks under its backdrop);
  // everything else goes straight to the OS picker.
  function chooseFiles() {
    setAttachError(null);
    if (categoryNeedsMetadata(effectiveCategory)) {
      setPickerOpen(false);
      setMetaModalOpen(true);
      return;
    }
    fileInputRef.current?.click();
  }

  function removeFile(idx) {
    const next = attachedFiles.filter((_, i) => i !== idx);
    setAttachedFiles(next);
    if (next.length === 0) {
      setFileCategory('');
      setOtherLabel('');
    }
    setPickerOpen(false);
    setAttachError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // Submit gate: pending attachments must have a category, and "other"
  // additionally needs a non-empty label. (Category is enforced before file
  // pick, but kept here as a defence-in-depth check.)
  const attachmentReady = attachedFiles.length === 0 || categoryReady;

  // How many case attachments already carry this category's token — the
  // -2/-3 numbering for new uploads continues from there.
  function existingTokenCount(token) {
    return existingAttachmentNames.reduce((n, name) => {
      const parsed = parseAttachmentName(name);
      return parsed?.token === token ? n + 1 : n;
    }, 0);
  }

  // Preview filenames — shown under the file chips so the user can confirm
  // what each file will actually be uploaded as. Mirrors the submit-loop
  // counter: existing same-token files, then +1 per file in this batch.
  const attachmentPreviewNames = (() => {
    if (!attachedFiles.length || !fileCategory) return null;
    if (isReqCategory) return null;   // per-case requirement files keep their names
    if (fileCategory === 'other' && !otherLabel.trim()) return null;
    const token = categoryToken({
      category: fileCategory,
      otherLabel: fileCategory === 'other' ? otherLabel.trim() : null,
    });
    const existing = existingTokenCount(token);
    return attachedFiles.map(({ file }, i) => {
      const suffix = existing + i > 0 ? `-${existing + i + 1}` : '';
      const ext = (file.name.match(/\.[^.]+$/) || [''])[0];
      return `AE${caseId}-${token}${suffix}${ext}`;
    });
  })();

  // Base name (no extension) the NEXT file of the current category will get —
  // the two-step dialog appends the chosen file's extension itself.
  const nextUploadBaseName = (() => {
    if (!fileCategory || isReqCategory) return null;
    const token = categoryToken({ category: fileCategory, otherLabel: null });
    const n = existingTokenCount(token) + attachedFiles.length;
    return `AE${caseId}-${token}${n > 0 ? `-${n + 1}` : ''}`;
  })();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Composer — hidden for any role that cannot post comments */}
      {canComment && (
        <form onSubmit={handleSubmit}>
          <div className="composer mb-4">
            <RichTextEditor
              placeholder="Add a comment…"
              value={commentText}
              onChange={setCommentText}
              rightSlot={canSeePrivate && (
                <>
                  <span className="info-tip">
                    <button type="button" className="info-tip-btn" aria-label="What do Public and Private mean?" tabIndex={0}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                    </button>
                    <span className="info-tip-pop" role="tooltip">
                      <span className="tip-row"><b>Public</b> — visible to anyone with access to this case.</span>
                      <span className="tip-row"><b>Private</b> — visible only to HRO, Admin, and Medical roles.</span>
                    </span>
                  </span>
                  <div className="vis-toggle">
                    <span
                      className={`vis-opt public${isPublic ? ' active' : ''}`}
                      onClick={() => setIsPublic(true)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      Public
                    </span>
                    <span
                      className={`vis-opt private${!isPublic ? ' active' : ''}`}
                      onClick={() => setIsPublic(false)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Private
                    </span>
                  </div>
                </>
              )}
            />

            {/* File chips — shown after files are chosen. Category is fixed at
                this point and shared by every file; upload metadata (taken-on /
                trainer + date) is each file's own and shows on its row. */}
            {attachedFiles.length > 0 && (
              <div style={{
                marginTop: 10,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 12px',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}>
                {attachedFiles.map(({ file, metadata }, i) => {
                  const previewName = attachmentPreviewNames?.[i] || file.name;
                  const metaLine    = attachmentMetadataLine(metadata, { formatDate });
                  return (
                    <div key={`${file.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{ width: 13, height: 13, flexShrink: 0, color: 'var(--brand)' }}>
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                      <span
                        title={[file.name !== previewName ? `was: ${file.name}` : null, metaLine].filter(Boolean).join(' · ') || undefined}
                        style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}
                      >
                        {previewName}
                        {file.size ? <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>{fileSizeLabel(file.size)}</span> : null}
                        {metaLine ? <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>· {metaLine}</span> : null}
                      </span>
                      <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${file.name}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-3)', lineHeight: 1 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
                <div style={{ fontSize: 11, color: 'var(--text-3)', paddingLeft: 21 }}>
                  {fileCategoryLabel}
                  {fileCategory === 'other' && otherLabel ? ` · "${otherLabel}"` : ''}
                  {isReqCategory ? ' · requested on this case' : ''}
                </div>
              </div>
            )}

            {/* Requirement chip — staged via the Requirement pop-up; created
                (and recorded as a comment) when the comment is posted. */}
            {stagedReq && (
              <div className="staged-chip">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0, color: 'var(--brand)' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                  <span style={{ flex: 1, color: 'var(--text)' }}>
                    {stagedReq.kind === REQ_KIND_SIGNOFF ? 'Sign-off requested' : 'Document requested'}: <strong style={{ fontWeight: 600 }}>{stagedReq.displayLabel}</strong>
                  </span>
                  <button type="button" onClick={openReqModal}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand)', fontSize: 12, fontWeight: 600, padding: 2 }}>
                    Edit
                  </button>
                  <button type="button" onClick={clearStagedReq} aria-label="Remove requirement"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-3)', lineHeight: 1 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', paddingLeft: 21 }}>
                  Added to “Required documents &amp; sign-offs” when you post.
                </div>
              </div>
            )}

            {/* Notify chip — recipients staged via the Notification pop-up; the
                email goes out when the comment is posted. */}
            {notifyCount > 0 && (
              <div className="staged-chip">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0, color: 'var(--brand)' }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  <span style={{ flex: 1, color: 'var(--text)' }}>
                    Notify {notifyCount} recipient{notifyCount > 1 ? 's' : ''} when posted
                  </span>
                  <button type="button" onClick={() => setNotifyModalOpen(true)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand)', fontSize: 12, fontWeight: 600, padding: 2 }}>
                    Edit
                  </button>
                  <button type="button" onClick={clearNotify} aria-label="Clear recipients"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-3)', lineHeight: 1 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                {notifyMessage.trim() && (
                  <div style={{ fontSize: 11, color: 'var(--text-3)', paddingLeft: 21 }}>“{notifyMessage.trim()}”</div>
                )}
              </div>
            )}

            {/* Snooze chip — staged from Add → "Snooze SLA notifications" (§4.1).
                Supervisors (manageSla) on a coded case, PUBLIC messages only:
                once the clinic acknowledges THIS message their response window
                starts after N calendar days. Per message — cleared on post. */}
            {snoozeVisible && snoozeOn && (
              <div className="staged-chip snooze">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
                  <MoonIcon size={13} weight="fill" style={{ flexShrink: 0, color: 'var(--brand)' }} />
                  <span style={{ color: 'var(--text)' }}>Snooze SLA notifications for</span>
                  <select
                    className="select"
                    aria-label="Snooze days"
                    value={snoozePick}
                    onChange={e => setSnoozePick(e.target.value)}
                    disabled={submitting}
                  >
                    {SNOOZE_DAY_PRESETS.map(d => <option key={d} value={String(d)}>{snoozeDaysLabel(d)}</option>)}
                    <option value="custom">Custom…</option>
                  </select>
                  {snoozePick === 'custom' && (
                    <>
                      <input
                        type="number"
                        className={`input days${snoozeInvalid ? ' invalid' : ''}`}
                        aria-label="Custom snooze days"
                        min={SNOOZE_DAYS_MIN}
                        max={SNOOZE_DAYS_MAX}
                        step={1}
                        inputMode="numeric"
                        placeholder="1–30"
                        value={snoozeCustom}
                        onChange={e => setSnoozeCustom(e.target.value)}
                        disabled={submitting}
                      />
                      <span style={{ color: 'var(--text-3)' }}>days</span>
                    </>
                  )}
                  <span style={{ flex: 1 }} />
                  <button type="button" onClick={() => setSnoozeOn(false)} aria-label="Remove snooze" disabled={submitting}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-3)', lineHeight: 1 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', paddingLeft: 21 }}>{snoozeHelperText(slaHours)}</div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <div className="composer-foot">
              {/* "+ Add" dropdown — Attachment / Requirement / Notification / Snooze,
                  each staging a chip above the footer so the composer stays uncluttered. */}
              <div className="add-menu-wrap">
                <button
                  ref={addBtnRef}
                  type="button"
                  className={`btn ghost sm${addMenuOpen ? ' add-menu-trigger-open' : ''}`}
                  onClick={toggleAddMenu}
                  disabled={submitting}
                  aria-haspopup="menu"
                  aria-expanded={addMenuOpen}
                  style={{ fontSize: 12 }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add
                  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {addMenuOpen && addMenuPos && createPortal(
                  <div
                    ref={addMenuRef}
                    className={`add-menu dd-shell${addMenuPos.up ? ' dd-up' : ''}`}
                    role="menu"
                    style={addMenuPos.up
                      ? { position: 'fixed', bottom: Math.round(window.innerHeight - addMenuPos.anchor.top + ADD_MENU_GAP), left: addMenuPos.left }
                      : { position: 'fixed', top: Math.round(addMenuPos.anchor.bottom + ADD_MENU_GAP), left: addMenuPos.left }}
                  >
                    <button type="button" className="add-menu-item dd-item" role="menuitem"
                      onClick={() => { setAddMenuOpen(false); setAttachError(null); setFileCategory(c => c || attachmentOptions[0]?.key || ''); setPickerOpen(true); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                      <span>Attachment<span className="add-menu-sub dd-sub">Upload a document or photo</span></span>
                    </button>
                    {canRequestRequirements && (
                      <button type="button" className="add-menu-item dd-item" role="menuitem" onClick={openReqModal}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                        <span>Requirement<span className="add-menu-sub dd-sub">Request a document or sign-off</span></span>
                      </button>
                    )}
                    <button type="button" className="add-menu-item dd-item" role="menuitem"
                      onClick={() => { setAddMenuOpen(false); setNotifyModalOpen(true); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                      </svg>
                      <span>Notification<span className="add-menu-sub dd-sub">Email people when this is posted</span></span>
                    </button>
                    {snoozeVisible && (
                      <button type="button" className="add-menu-item dd-item" role="menuitem"
                        onClick={() => { setAddMenuOpen(false); setSnoozeOn(true); }}>
                        <MoonIcon size={15} />
                        <span>Snooze SLA notifications<span className="add-menu-sub dd-sub">Pause the clinic's clock once they acknowledge</span></span>
                      </button>
                    )}
                  </div>,
                  document.body
                )}
              </div>

              <button
                type="submit"
                className="btn primary sm"
                disabled={submitting || (!richToText(commentText) && !stagedReq) || !attachmentReady || snoozeInvalid}
                title={!attachmentReady ? 'Pick a category for the attached file first.'
                  : snoozeInvalid ? `Snooze days must be a whole number between ${SNOOZE_DAYS_MIN} and ${SNOOZE_DAYS_MAX}.`
                  : undefined}
              >
                {submitting ? 'Posting…' : 'Post comment'}
              </button>
            </div>
          </div>

          {/* ── Attachment pop-up ── (category-first picker) */}
          {pickerOpen && (
            <ModalShell title="Add attachment" busy={false} onClose={closePicker}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  What is this file? <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <SearchableSelect
                  name="comment-file-category"
                  value={fileCategory}
                  onChange={e => { setFileCategory(e.target.value); if (e.target.value !== 'other') setOtherLabel(''); }}
                  placeholder="Select category…"
                  options={attachmentOptions.map(opt => ({
                    value: opt.key,
                    label: opt.label,
                    sub:   opt.sub,
                  }))}
                />
              </div>

              {fileCategory === 'other' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                    Describe the file <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    className="input"
                    autoFocus
                    value={otherLabel}
                    onChange={e => setOtherLabel(e.target.value)}
                    placeholder="e.g. Witness statement, X-ray, photo of arm"
                    maxLength={60}
                    style={{ fontSize: 13, padding: '7px 10px' }}
                  />
                </div>
              )}

              {attachError && (
                <div className="callout danger" style={{ fontSize: 12 }}>{attachError}</div>
              )}

              {categoryReady && categoryNeedsMetadata(effectiveCategory) && (
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {fileCategoryLabel || 'This document'} needs a few details with the file
                  {effectiveCategory === 'post_photo' ? ' (when the photo was taken)' : effectiveCategory === 'training_certificate' ? ' (training date and trainer)' : ''} — the next step asks for them.
                </div>
              )}

              {categoryReady && !isPublic && fileCategory !== 'other' && (
                <div className="callout warn" style={{ fontSize: 12 }}>
                  This comment is Private. Requested documents must go on a public message so the clinic can see them — switch to Public before posting, or attach the file as “Other”.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn ghost sm" onClick={closePicker}>
                  Cancel
                </button>
                <button type="button" className="btn primary sm" disabled={!categoryReady} onClick={chooseFiles}>
                  Choose files…
                </button>
              </div>
            </ModalShell>
          )}

          {/* ── File + metadata dialog ── (post photo taken-on / training
              certificate trainer + date). Opens INSTEAD of the picker, above
              every composer overlay; Cancel steps back to the picker. */}
          {metaModalOpen && !pickerOpen && fileCategory && (
            <SlotUploadModal
              open
              category={effectiveCategory}
              label={fileCategoryLabel}
              fileNamePreview={nextUploadBaseName}
              multiple
              incidentDate={dateOfIncident || null}
              zIndex={UPLOAD_MODAL_Z}
              onCancel={() => { setMetaModalOpen(false); setPickerOpen(true); }}
              onConfirm={handleMetaUploadConfirm}
            />
          )}

          {/* ── Notification pop-up ── (recipients + optional note; sent on Post) */}
          {notifyModalOpen && (
            <ModalShell title="Notify recipients" busy={false} onClose={() => setNotifyModalOpen(false)} maxWidth={520}>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Chosen people are emailed when you post the comment — with the case reference and a link only, never client details.
              </div>
              {renderRecipientPicker()}
              <textarea
                className="textarea"
                placeholder="Add a note for recipients (optional)…"
                value={notifyMessage}
                onChange={e => setNotifyMessage(e.target.value)}
                style={{ minHeight: 60, fontSize: 12 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <button type="button" className="btn ghost sm" onClick={clearNotify} disabled={notifyCount === 0}>
                  Clear
                </button>
                <button type="button" className="btn primary sm" onClick={() => setNotifyModalOpen(false)}>
                  {notifyCount > 0 ? `Done · ${notifyCount}` : 'Done'}
                </button>
              </div>
            </ModalShell>
          )}

          {/* ── Requirement pop-up ── (stages a document/sign-off onto the comment) */}
          {reqModalOpen && (
            <ModalShell title="Add requirement" busy={false} onClose={closeReqModal}>
              {/* Type chooser */}
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button"
                  className={`chip${reqKind === REQ_KIND_DOCUMENT ? ' active' : ''}`}
                  onClick={() => { setReqKind(REQ_KIND_DOCUMENT); setReqError(null); }}>
                  Request Document
                </button>
                <button type="button"
                  className={`chip${reqKind === REQ_KIND_SIGNOFF ? ' active' : ''}`}
                  onClick={() => { setReqKind(REQ_KIND_SIGNOFF); setReqError(null); }}>
                  Request Sign-off
                </button>
              </div>

              {/* Document type */}
              {reqKind === REQ_KIND_DOCUMENT && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Document to request</label>
                  <SearchableSelect
                    name="requirement-document-type"
                    value={reqKey}
                    onChange={e => { setReqKey(e.target.value); setReqError(null); }}
                    placeholder="Choose a document type…"
                    options={[
                      ...(requirementTypes || []).map(t => ({ value: t.key, label: t.label })),
                      { value: REQ_OTHER_KEY, label: 'Other (specify)' },
                    ]}
                  />
                  {reqKey === REQ_OTHER_KEY && (
                    <input
                      type="text"
                      className="input"
                      placeholder="Describe the document…"
                      value={reqOtherLabel}
                      onChange={e => setReqOtherLabel(e.target.value)}
                      maxLength={120}
                      style={{ fontSize: 13, padding: '7px 10px' }}
                    />
                  )}
                </div>
              )}

              {/* Sign-off task */}
              {reqKind === REQ_KIND_SIGNOFF && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Task to sign off</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g. Confirm corrective action completed"
                    value={reqSignoffLabel}
                    onChange={e => { setReqSignoffLabel(e.target.value); setReqError(null); }}
                    maxLength={120}
                    style={{ fontSize: 13, padding: '7px 10px' }}
                  />
                </div>
              )}

              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                Posting your comment creates this {reqKind === REQ_KIND_SIGNOFF ? 'sign-off task' : 'required-upload slot'} in “Required documents &amp; sign-offs” and records it in the thread. To tell someone, also use <b style={{ fontWeight: 600 }}>Add → Notification</b>.
              </div>

              {reqError && <div className="callout danger" style={{ fontSize: 12 }}>{reqError}</div>}

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn ghost sm" onClick={closeReqModal}>
                  Cancel
                </button>
                <button type="button" className="btn primary sm" onClick={stageRequirement}>
                  {stagedReq ? 'Update request' : 'Add to comment'}
                </button>
              </div>
            </ModalShell>
          )}

          {submitError && (
            <div className="callout danger mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>{submitError}</div>
            </div>
          )}

          {submitSuccess && (
            <div className="callout brand mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <div>Comment posted successfully.</div>
            </div>
          )}

        </form>
      )}

      {/* Comment list */}
      {loading && (
        <p className="muted" style={{ padding: '16px 0' }}>Loading comments…</p>
      )}

      {!loading && error && (
        <div className="callout danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div><strong>Could not load comments</strong> — {error}</div>
        </div>
      )}

      {!loading && !error && comments.length === 0 && (
        <p className="muted" style={{ padding: '20px 0', textAlign: 'center' }}>
          No messages yet.
        </p>
      )}

      {!loading && !error && comments.length > 0 && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            margin: '20px 0 16px',
          }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            <span style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {comments.length} {comments.length === 1 ? 'message' : 'messages'}
            </span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                canSeePrivate={canSeePrivate}
                onAck={canAcknowledge && comment._source === 'comment' && comment.authorSide === 'hq' ? handleAck : null}
                snoozeBadge={comment._source === 'comment' ? commentSnoozeBadge(comment, sla, { formatDateTime, formatDate: formatDate }) : null}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function fmtUploadMeta(att) {
  const parts = [];
  if (att.uploadedBy) parts.push(att.uploadedBy);
  if (att.uploadedAt) {
    const d = new Date(att.uploadedAt);
    parts.push(d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }));
  }
  return parts.join(' · ');
}

/**
 * Individual comment item. `snoozeBadge` (lib/sla.js commentSnoozeBadge) is the
 * §4.1 snooze this message carries — pending ("Snoozes the response clock for N
 * days once acknowledged") or applied ("Snoozed until <date>") — or null.
 */
/**
 * Acknowledge button — a stateful pill: idle "Acknowledge" → loading spinner →
 * success (collapses to a green circle with a drawn check, the end state) or
 * error (red, shake, then back to idle). Adapted from the product owner's
 * reference markup; sized for the comment header. `aria-live` announces the
 * state change to screen readers.
 */
function AckButton({ state, onClick, title, readOnly = false }) {
  const cls = `sbtn${state === 'loading' ? ' is-loading' : ''}${state === 'success' ? ' is-success' : ''}${state === 'error' ? ' is-error' : ''}${readOnly ? ' is-readonly' : ''}${state === 'idle' && !readOnly ? ' pending-glow' : ''}`;
  const on = (name) => (state === name || (name === 'idle' && state === 'disabled') ? ' on' : '');
  return (
    <button
      type="button"
      className={cls}
      disabled={state === 'disabled'}
      aria-live="polite"
      aria-pressed={state === 'success'}
      aria-label={state === 'success' ? (readOnly ? 'Acknowledged by the clinic' : 'Acknowledged') : state === 'loading' ? 'Acknowledging' : state === 'error' ? 'Could not acknowledge' : 'Acknowledge this message'}
      title={title}
      tabIndex={readOnly ? -1 : undefined}
      onClick={state === 'idle' && !readOnly ? onClick : undefined}
    >
      <span className={`layer${on('idle')}`} data-layer="idle">Acknowledge</span>
      <span className={`layer${on('loading')}`} data-layer="loading"><span className="spinner" /></span>
      <span className={`layer${on('success')}`} data-layer="success">
        <svg className="check" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 13 l4 4 L19 7" /></svg>
      </span>
      <span className={`layer${on('error')}`} data-layer="error">
        <svg className="xmark" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6 L18 18 M18 6 L6 18" /></svg>
      </span>
    </button>
  );
}

function CommentItem({ comment, canSeePrivate, onAck = null, snoozeBadge = null }) {
  const isPrivate     = !comment.isPublic;
  const hasAttachment = comment.attachments?.length > 0;
  const [downloading, setDownloading] = useState(null);
  const [previewAtt,  setPreviewAtt]  = useState(null);
  const [acking,      setAcking]      = useState(false);
  const [ackError,    setAckError]    = useState(null);
  // The button shows the error state (red, shake) briefly, then returns to idle
  // so the person can try again; the message stays available in the tooltip.
  useEffect(() => {
    if (!ackError) return undefined;
    const t = setTimeout(() => setAckError(null), 1800);
    return () => clearTimeout(t);
  }, [ackError]);
  if (!canSeePrivate && !comment.isPublic) return null;

  const ack = ackSummary(comment.acks, { formatDateTime });
  async function acknowledge() {
    if (!onAck || ack.mine || acking) return;
    setAcking(true); setAckError(null);
    try { await onAck(comment.id); }
    catch (e) { setAckError(e.message || 'Could not acknowledge'); }
    finally { setAcking(false); }
  }

  async function download(att) {
    if (downloading) return;
    const url = att.downloadUrl || att.contentUrl;

    // SAS URL — download directly, no auth header needed
    if (url && url.startsWith('https://') && url.includes('.blob.core.windows.net')) {
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // Legacy SP path
    const spUrl = att.serverRelativeUrl;
    if (!spUrl) { alert(`No download URL for "${att.name}"`); return; }
    const absUrl = spUrl.startsWith('http')
      ? spUrl
      : `https://therapieclinic.sharepoint.com${spUrl}`;
    setDownloading(att.name);
    try {
      const blob    = await fetchSpBlob(absUrl);
      const blobUrl = URL.createObjectURL(blob);
      const a       = document.createElement('a');
      a.href = blobUrl; a.download = att.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      alert(`Could not download "${att.name}": ${err.message}`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div style={{
      background:   isPrivate ? 'rgba(245,158,11,0.06)' : 'var(--surface-2)',
      border:       `1px solid ${isPrivate ? 'rgba(245,158,11,0.35)' : 'var(--border)'}`,
      borderLeft:   isPrivate ? '3px solid #F59E0B' : '1px solid var(--border)',
      borderRadius: '10px',
      padding:      '14px 16px',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
        <span className="badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px' }}>
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          {formatDateTime(comment.created)}
        </span>
        <span className="badge" style={{ fontWeight: 600, color: 'var(--text)' }}>
          {comment.authorName || 'Unknown'}
        </span>
        {/* Attachment indicator */}
        {hasAttachment && (
          <span className="badge" style={{ color: 'var(--brand)' }} title={`${comment.attachments.length} attachment${comment.attachments.length > 1 ? 's' : ''}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px' }}>
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            {comment.attachments.length}
          </span>
        )}
        {comment._source === 'description' && (
          <span className="badge" style={{ color: 'var(--text-3)', fontSize: 10 }}>
            SP field history
          </span>
        )}
        {/* Snooze (§4.1): this message snoozes the response clock once the clinic
            acknowledges it — "Snoozed until <date>" after that ack lands. */}
        {snoozeBadge && (
          <span
            className={`badge snooze${snoozeBadge.state === 'snoozed' ? ' applied' : ' pending-glow'}`}
            title={snoozeBadge.title}
          >
            <MoonIcon size={10} weight={snoozeBadge.state === 'snoozed' ? 'fill' : 'regular'} />
            {snoozeBadge.text}
          </span>
        )}
        {canSeePrivate && (
          isPrivate ? (
            <span className="badge vis-private">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px' }}>
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Private
            </span>
          ) : (
            <span className="badge vis-public">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px' }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              Public
            </span>
          )
        )}
        {/* Acknowledge ("like") — add-only, never notifies; names + times on hover.
            Own ack highlighted + disabled. Pauses the response timer when it is
            this side's turn (sla_escalation_logic.md §4). It is the row's ONE
            action, so it sits at the right end, apart from the descriptive
            badges (time · author · files · visibility). */}
        {(onAck || ack.count > 0) && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', paddingLeft: 8 }}>
            {onAck ? (
              <AckButton
                state={acking ? 'loading' : ackError ? 'error' : ack.mine ? 'success' : 'idle'}
                title={ackError || ack.title || 'Acknowledge this message'}
                onClick={acknowledge}
              />
            ) : (
              <AckButton state="success" readOnly title={ack.title} />
            )}
          </span>
        )}
      </div>

      {/* Body */}
      {isHtml(comment.text) ? (
        <div
          className="rich-content"
          style={{ wordBreak: 'break-word', marginBottom: hasAttachment ? 10 : 0 }}
          dangerouslySetInnerHTML={{ __html: sanitizeRich(comment.text) }}
        />
      ) : (
        <p style={{
          margin: 0,
          fontSize: '13.5px',
          color: 'var(--text)',
          lineHeight: '1.75',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          marginBottom: hasAttachment ? 10 : 0,
        }}>
          {comment.text}
        </p>
      )}

      {/* File preview modal */}
      {previewAtt && (
        <FilePreview
          url={previewAtt.downloadUrl || previewAtt.contentUrl}
          name={previewAtt.name}
          onClose={() => setPreviewAtt(null)}
        />
      )}

      {/* Attached files — compact pills, wrapping */}
      {hasAttachment && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          {comment.attachments.map((att, i) => {
            const meta       = fmtUploadMeta(att);
            const metaLine   = attachmentMetadataLine(att.metadata, { formatDate });
            const previewable = canPreview(att.name);
            return (
              <button
                key={i}
                type="button"
                onClick={() => previewable ? setPreviewAtt(att) : download(att)}
                disabled={!!downloading}
                title={[att.name, metaLine, meta].filter(Boolean).join(' · ')}
                style={{
                  display:      'inline-flex',
                  alignItems:   'center',
                  gap:          5,
                  padding:      '3px 9px',
                  borderRadius: 20,
                  border:       '1px solid var(--border)',
                  background:   'var(--surface)',
                  cursor:       downloading ? 'wait' : 'pointer',
                  fontSize:     11,
                  color:        'var(--brand)',
                  fontWeight:   500,
                  maxWidth:     metaLine ? 360 : 220,
                  opacity:      downloading === att.name ? 0.6 : 1,
                  transition:   'opacity .15s, background .15s',
                  whiteSpace:   'nowrap',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, flexShrink: 0 }}>
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {downloading === att.name ? 'Downloading…' : att.name}
                </span>
                {metaLine && downloading !== att.name && (
                  <span style={{ color: 'var(--text-3)', fontWeight: 400, flexShrink: 0 }}>· {metaLine}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
